import { useState } from "react";
import { Text, Flex } from "rebass/styled-components";
import useTranslation from "next-translate/useTranslation";

import SitterCard from "./SitterCard";
import ResultListPlaceholder from "./ResultListPlaceholder";
import Button from "../common/Button";
import Pagination from "../common/Pagination";
import { useIsMobile } from "../../lib/media";
import { useSearchState, stateToQueryParams } from "./SearchContext";

interface Props {
  loading: boolean;
  searchResults: any;
  error: any;
  onSitterMouseEnter?: (index: number) => void;
  onSitterMouseLeave?: (index: number) => void;
  isSEOLanding?: boolean;
}

export default function SearchResultList({
  loading,
  searchResults,
  isSEOLanding = false,
  ...props
}: Props) {
  const isMobile = useIsMobile();
  const { t } = useTranslation("common");
  const { t: tSearch } = useTranslation("search");
  const searchContextState = useSearchState();
  const { page, ...queryParams } = stateToQueryParams(searchContextState);

  const [viewAllResults, setViewAllResults] = useState(
    !isSEOLanding ||
      (page && page !== 1) ||
      searchResults?.city?.stats ||
      !searchResults?.city
  );
  const showAllResults = () => setViewAllResults(true);

  if (loading || !searchResults) return <ResultListPlaceholder />;

  const { caregivers, totalPages, currentPage, serviceType } = searchResults;

  const visibleCaregivers = viewAllResults
    ? caregivers
    : caregivers.slice(0, 5);

  return (
    <>
      {visibleCaregivers.length === 0 && (
        <Text pt={1} pr={2}>
          {tSearch("noResults")}
        </Text>
      )}

      {visibleCaregivers.map((caregiver, index) => {
        const {
          name,
          avatar,
          caregiverHeadline,
          caregiverReviewsCount,
          caregiverReviewsScore,
          caregiverCompletedBookingsCount,
          slug,
          publicAddress,
          availableHolidays,
          availablePromotions,
          calendarUpToDate,
          reviews,
          city,
          featuredReview,
          searchDistanceAwayKm
        } = caregiver;

        const belongsToCity = city?.name === queryParams.near;

        return (
          <SitterCard
            key={index}
            name={name}
            headline={caregiverHeadline}
            avatar={avatar.circle}
            price={caregiver.price}
            minPrice={caregiver.minPrice}
            repeatCustomersCount={caregiver.repeatCustomersCount}
            priceCurrency={caregiver.currency}
            pricePer={serviceType && t(serviceType.pricePer)}
            bookingEstimation={caregiver.bookingEstimation}
            reviewsCount={caregiverReviewsCount}
            reviewsScore={caregiverReviewsScore}
            completedBookingsCount={caregiverCompletedBookingsCount}
            slug={slug}
            queryParams={queryParams}
            address={publicAddress}
            holiday={availableHolidays}
            availablePromotions={availablePromotions}
            city={city}
            calendarUpToDate={calendarUpToDate}
            featuredReview={featuredReview}
            searchDistanceAwayKm={!belongsToCity && searchDistanceAwayKm}
            reviews={reviews}
            hideHover={isMobile}
            onMouseEnter={e => {
              e.stopPropagation();
              props.onSitterMouseEnter(index);
            }}
            onMouseLeave={e => {
              e.stopPropagation();
              props.onSitterMouseLeave(index);
            }}
            openInNewTab={!isMobile}
            shouldPrioritizeImage={index < 4} // images above the fold
            availableServices={caregiver.services}
            isSuperCaregiver={caregiver?.isSuperCaregiver}
          />
        );
      })}

      {!viewAllResults && caregivers.length > 5 ? (
        <Flex my={4} justifyContent="center">
          <Button onClick={showAllResults} w="100%">
            {t("common:viewAllResultsNear", {
              caregiversCount: searchResults?.totalEntries,
              near: queryParams.near,
              city: queryParams.near
            })}
          </Button>
        </Flex>
      ) : (
        <Pagination
          totalPages={totalPages}
          maxPages={isMobile ? 5 : 8}
          currentPage={currentPage}
        />
      )}
    </>
  );
}
